/**
 * キャンバス - フィールドの一覧表示と並び替え
 */

import { useState } from 'react'
import { GripVertical, Trash2 } from 'lucide-react'
import type { FormField } from '../../types/formBuilder'
import { FIELD_TYPES } from '../../types/formBuilder'

interface CanvasProps {
  fields: FormField[]
  selectedFieldId: string | null
  onSelectField: (fieldId: string) => void
  onDeleteField: (fieldId: string) => void
  onReorderFields: (startIndex: number, endIndex: number) => void
}

export function Canvas({
  fields,
  selectedFieldId,
  onSelectField,
  onDeleteField,
  onReorderFields,
}: CanvasProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      onReorderFields(draggedIndex, dropIndex)
    }
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const getFieldIcon = (type: string) => {
    const fieldType = FIELD_TYPES.find(ft => ft.type === type)
    return fieldType?.icon
  }

  const getFieldLabel = (type: string) => {
    const fieldType = FIELD_TYPES.find(ft => ft.type === type)
    return fieldType?.label || type
  }

  if (fields.length === 0) {
    return (
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1rem'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '0.25rem'
          }}>フォームフィールド</h3>
          <p style={{
            fontSize: '0.75rem',
            color: '#6b7280'
          }}>0個のフィールド</p>
        </div>
        <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <p className="text-gray-500 text-sm">
            左側のパレットからフィールドを追加してください
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      padding: '1rem'
    }}>
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#374151',
          marginBottom: '0.25rem'
        }}>フォームフィールド</h3>
        <p style={{
          fontSize: '0.75rem',
          color: '#6b7280'
        }}>{fields.length}個のフィールド</p>
      </div>
      <div className="space-y-3">
      {fields.map((field, index) => {
        const Icon = getFieldIcon(field.type)
        return (
          <div
            key={field.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              backgroundColor: 'white',
              border: selectedFieldId === field.id
                ? '2px solid #3b82f6'
                : dragOverIndex === index
                  ? '2px solid #93c5fd'
                  : '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              padding: '1rem',
              cursor: 'pointer',
              opacity: draggedIndex === index ? 0.5 : 1,
              transition: 'all 0.15s',
              boxShadow: selectedFieldId === field.id ? '0 4px 6px -1px rgba(59, 130, 246, 0.1)' : 'none'
            }}
            onClick={() => onSelectField(field.id)}
            onMouseEnter={(e) => {
              if (selectedFieldId !== field.id && draggedIndex === null) {
                e.currentTarget.style.borderColor = '#d1d5db'
              }
            }}
            onMouseLeave={(e) => {
              if (selectedFieldId !== field.id && dragOverIndex !== index) {
                e.currentTarget.style.borderColor = '#e5e7eb'
              }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              {/* ドラッグハンドル */}
              <div
                style={{
                  cursor: 'grab',
                  color: '#9ca3af',
                  flexShrink: 0,
                  paddingTop: '0.125rem'
                }}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <GripVertical size={20} />
              </div>

              {/* アイコン */}
              <div style={{
                width: '2.5rem',
                height: '2.5rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f3f4f6',
                borderRadius: '0.375rem',
                flexShrink: 0
              }}>
                {Icon && <Icon size={18} strokeWidth={2} color="#6b7280" />}
              </div>

              {/* フィールド情報 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>
                    {field.label || '（ラベル未設定）'}
                  </span>
                  {field.required && (
                    <span style={{
                      fontSize: '0.75rem',
                      backgroundColor: '#fee2e2',
                      color: '#991b1b',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontWeight: 500
                    }}>
                      必須
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                  <span>{getFieldLabel(field.type)}</span>
                  <span style={{ margin: '0 0.5rem' }}>•</span>
                  <span>key: {field.name || '（未設定）'}</span>
                </div>
                {field.description && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    {field.description}
                  </div>
                )}
              </div>

              {/* アクションボタン */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm(`フィールド「${field.label}」を削除しますか？`)) {
                      onDeleteField(field.id)
                    }
                  }}
                  style={{
                    padding: '0.5rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: '#dc2626',
                    cursor: 'pointer',
                    borderRadius: '0.375rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fee2e2'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }}
                  title="削除"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        )
      })}
      </div>
    </div>
  )
}
